import React from 'react';
import { cn } from '../lib/utils';

/**
 * EffectText — renders catalog effect markup the way it reads on the card.
 *
 *   [SIN] [DBL] [TRP] [QUAD]   stack requirement   → "1" "2" "3" "4" stack chips
 *   [Activate] (Once per turn) ability keywords    → keyword pills
 *   《When this … enters play》 trigger              → teal trigger pill
 *   【POWER】                    TYPE               → outlined type pill
 *   『TIGA』                     character name     → character pill
 *   [ANY:2] [Your … Step] …    other bracket tags  → muted pill
 *
 * Anything else renders as plain text, so unknown markup degrades to readable text.
 */
const TOKEN = /(\[(?:SIN|DBL|TRP|QUAD)\]|\[[^\]\n]+\]|\(Once per turn\)|《[^》\n]+》|【[^】\n]+】|『[^』\n]+』)/g;

const STACKS = { '[SIN]': '1', '[DBL]': '2', '[TRP]': '3', '[QUAD]': '4' };

const pill = 'inline-flex items-center rounded px-1.5 py-px mx-0.5 text-[0.85em] font-semibold leading-tight align-baseline whitespace-nowrap';

function renderToken(token, key) {
  if (STACKS[token]) {
    return (
      <span
        key={key}
        title={`Requires a stack of ${STACKS[token]}`}
        className={cn(pill, 'bg-foreground text-background px-1 tabular-nums')}
      >
        ▮{STACKS[token]}
      </span>
    );
  }
  if (token === '[Activate]') {
    return <span key={key} className={cn(pill, 'bg-sky-700 text-white')}>Activate</span>;
  }
  if (token === '(Once per turn)') {
    return <span key={key} className={cn(pill, 'bg-zinc-500 text-white')}>Once per turn</span>;
  }
  const inner = token.slice(1, -1);
  if (token.startsWith('《')) {
    return <span key={key} className={cn(pill, 'bg-teal-600 text-white whitespace-normal')}>{inner}</span>;
  }
  if (token.startsWith('【')) {
    return <span key={key} className={cn(pill, 'border border-current text-foreground/80')}>{inner}</span>;
  }
  if (token.startsWith('『')) {
    return <span key={key} className={cn(pill, 'bg-primary/15 text-primary')}>{inner}</span>;
  }
  return <span key={key} className={cn(pill, 'bg-muted text-muted-foreground whitespace-normal')}>{inner}</span>;
}

export function tokenizeEffect(text) {
  return String(text).split(TOKEN).filter(Boolean);
}

export default function EffectText({ text, className }) {
  if (!text) return null;
  return (
    <div className={cn('space-y-1.5', className)}>
      {String(text).split('\n').map((line, i) => (
        <p key={i}>
          {tokenizeEffect(line).map((part, j) =>
            part.match(TOKEN) ? renderToken(part, j) : <React.Fragment key={j}>{part}</React.Fragment>
          )}
        </p>
      ))}
    </div>
  );
}
